import classNames from 'clsx';
import flow from 'lodash/flow';
import get from 'lodash/get';
import { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import playerStatuses from 'src/common/utils/playerStatuses';
import { isLarge } from 'src/common/utils/breakpoints';
import * as localization from 'src/common/constants/localizations/player';
import { WEB_OPEN_INSTALL_APP_DIALOG_SHOW_PLAYER } from 'src/common/constants/experiments/dialog';
import connectWithExperiments from '../../decorators/connectWithExperiments';
import { selectIsDesktopNowPlayingEnabled } from '../../selectors/config';

import Text from '../Text';
import PlayErrorIcon from '../shared/svgIcons/player/PlayErrorIcon';
import MessagePopover from '../../components/messagePopover/MessagePopover';
import { setHasAdBlocker } from '../../actions/player';
import { sharingFromPlayer } from '../../actions/share';
import isAdBlockEnabled from '../utils/isAdBlockEnabled';
import PlayButton from './controls/playButton/PlayButton';
import Scrubber from './controls/scrubber/Scrubber';
import NowPlaying from './nowPlaying/NowPlaying';
import ActionDrawer from './actionDrawer/ActionDrawer';

import css from './player.module.scss';

export function mapStateToProps(state) {
  const { app, preroll, player } = state;
  const isMobile = get(app, 'isMobile');

  const {
    nowPlaying,
    playerStatus,
    guideItemPathname,
    tunedGuideId,
    hasAdBlocker,
  } = player;

  return {
    isMobile,
    nowPlaying,
    guideItemPathname,
    playerStatus,
    tunedGuideId,
    hasAdBlocker,
    isPrerollLoaded: get(preroll, 'playerState') === playerStatuses.loaded,
    isDesktopNowPlayingEnabled: selectIsDesktopNowPlayingEnabled(state),
  };
}

export function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        setHasAdBlocker,
        sharingFromPlayer,
      },
      dispatch,
    ),
  };
}

export const ErrorMessage = ({ customMessage }) => (
  <div data-testid="playerErrorMessage" className={css.informational}>
    <div className={css.errorIcon}>
      <PlayErrorIcon width="16px" height="16px" />
    </div>
    <div className={css.errorMessageContainer}>
      {customMessage ? (
        <Text name={customMessage} />
      ) : (
        <Text name={localization.ERROR_MESSAGE} />
      )}
    </div>
  </div>
);

ErrorMessage.propTypes = {
  customMessage: PropTypes.string,
};

export const PopOutMessage = () => (
  <div data-testid="playerPopOutMessage" className={css.informational}>
    <Text name={localization.POP_OUT_MESSAGE} />
  </div>
);

export class Player extends Component {
  static propTypes = {
    isMobile: PropTypes.bool.isRequired,
    actions: PropTypes.object.isRequired,
    nowPlaying: PropTypes.object.isRequired,
    isPrerollLoaded: PropTypes.bool.isRequired,
    guideItemPathname: PropTypes.string,
    playerStatus: PropTypes.string.isRequired,
    tunedGuideId: PropTypes.string.isRequired,
    breakpoint: PropTypes.number.isRequired,
    hasAdBlocker: PropTypes.bool,
    [WEB_OPEN_INSTALL_APP_DIALOG_SHOW_PLAYER]: PropTypes.bool,
    isDesktopNowPlayingEnabled: PropTypes.bool,
  };

  componentDidMount() {
    isAdBlockEnabled(this.isAdBlockEnabledCallback);
  }

  isAdBlockEnabledCallback = (hasAdBlocker) => {
    const { actions } = this.props;
    actions.setHasAdBlocker(hasAdBlocker);
  };

  render() {
    const {
      actions,
      nowPlaying,
      guideItemPathname,
      isMobile,
      playerStatus,
      tunedGuideId,
      hasAdBlocker,
      isPrerollLoaded,
      breakpoint,
      [WEB_OPEN_INSTALL_APP_DIALOG_SHOW_PLAYER]: showPlayerOverDialog,
      isDesktopNowPlayingEnabled,
    } = this.props;
    let StatusMessage;
    let scrubberContent = <Scrubber />;

    if (playerStatus === playerStatuses.failed) {
      const errorMessage = (
        <div className={css.messageContainer}>
          <ErrorMessage
            customMessage={
              hasAdBlocker ? localization.AD_BLOCKER_ERROR_MESSAGE : ''
            }
          />
        </div>
      );
      scrubberContent = errorMessage;
      StatusMessage = errorMessage;
    } else if (playerStatus === playerStatuses.popout) {
      scrubberContent = (
        <div className={css.messageContainer}>
          <PopOutMessage />
        </div>
      );
      StatusMessage = <PopOutMessage />;
    }

    return (
      <div
        data-testid="player"
        className={classNames(css.playerContainer, {
          [css.playerHighestIndex]: showPlayerOverDialog,
          [css.desktopPlayerContainer]: isDesktopNowPlayingEnabled,
        })}
      >
        <div className={css.playerInner}>
          <div className={css.leftSection}>
            {StatusMessage && (
              <div className={css.leftErrorMessageContainer}>
                {StatusMessage}
              </div>
            )}
            <div
              data-testid="nowPlayingContainer"
              data-guideid={tunedGuideId}
              className={classNames(css.nowPlayingContainer, {
                [css.hideSmall]: !!StatusMessage,
              })}
            >
              <NowPlaying
                nowPlaying={nowPlaying}
                nowPlayingPathname={guideItemPathname}
                isMobile={isMobile}
                playerStatus={playerStatus}
                isPrerollLoaded={isPrerollLoaded}
              />
            </div>
          </div>
          <div className={css.rightSection}>
            <div className={css.controlsContainer}>
              {isLarge(breakpoint) && (
                <MessagePopover
                  className={css.playerMessage}
                  isPlayerOpen={playerStatus !== playerStatuses.idle}
                />
              )}
              <div className={css.playButtonContainer}>
                <PlayButton breakpoint={breakpoint} />
              </div>
              <div
                className={classNames(css.scrubberContainer, {
                  [css.hideMedium]: !StatusMessage,
                })}
                data-testid="scrubberContainer"
              >
                {scrubberContent}
              </div>
            </div>
            <div className={css.actionsSpacer} />
            <div className={css.actionsContainer}>
              <ActionDrawer
                sharingFromPlayer={() => actions.sharingFromPlayer()}
                tunedGuideId={tunedGuideId}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default flow(
  connect(mapStateToProps, mapDispatchToProps),
  connectWithExperiments([WEB_OPEN_INSTALL_APP_DIALOG_SHOW_PLAYER]),
)(Player);
